import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { array, object } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, numberScheme, stringScheme, urlScheme } from 'src/utils/validation-schemes';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { useUpdateKnowledgeBaseCollectorMutation } from 'src/services/knowledgeBaseService';
import {
  WEBSITE_INITIAL_REQUEST,
  WEBSITE_INITIAL_REQUEST_V2,
  WEBSITE_INTERVAL,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { CloseIcon } from 'src/assets/icons/CloseIcon';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { API_VERSION } from 'src/constants/api-routes';

export const EditWebsitePopup = ({
  knowledgeBaseId = null,
  trigger,
  closeSourceModal = () => {},
  item = {} as any,
  disabled = false,
}) => {
  const { setSkipRefetchFiles } = useKBContext();
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV1 = version === API_VERSION.V1;
  const [
    updateKnowledgeBaseCollectorMutation,
    {
      isLoading: isLoadingUpdateKnowledgeBaseCollector,
      isSuccess: isSuccessUpdateKnowledgeBaseCollector,
    },
  ] = useUpdateKnowledgeBaseCollectorMutation();
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues: {
      urls: item?.data?.urls?.length
        ? item.data.urls.map((url) => {
            return { value: url };
          })
        : [{ value: '' }],
      exclude_urls: item?.data?.exclude_urls?.length
        ? item.data.exclude_urls.map((url) => {
            return { value: url };
          })
        : [],
      name: item.data.name || '',
      autoSync: item.data.schedule ? item.data.schedule.enabled : true,
      recursive: item.data.recursive,
      interval: item.data.schedule
        ? isV1
          ? item.data.schedule.interval
          : WEBSITE_INTERVAL.WEEKLY
        : isV1
          ? WEBSITE_INTERVAL.MONTHLY
          : WEBSITE_INTERVAL.WEEKLY,
    },
    resolver: yupResolver(
      object({
        name: stringScheme({ required: true }).label('Name'),
        urls: array()
          .of(
            object({
              value: urlScheme({ required: true }).label('URL'),
            }),
          )
          .min(1, 'At least one URL is required'),
        exclude_urls: array().of(
          object({
            value: urlScheme({ required: false }).label('Exclude URL'),
          }),
        ),
        recursive: booleanScheme({ required: false }).label('Sync Nested Pages'),
        autoSync: booleanScheme({ required: false }).label('AutoSync'),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'urls',
  });

  const {
    fields: excludeFields,
    append: appendExclude,
    remove: removeExclude,
  } = useFieldArray({
    control,
    name: 'exclude_urls',
  });

  const resetToInitial = (trial = false) => {
    reset({
      urls: isV1
        ? item?.data?.urls?.length
          ? item.data.urls.map((url) => {
              return { value: url };
            })
          : [{ value: '' }]
        : [{ value: item?.data?.url }],
      exclude_urls: item?.data?.exclude_urls?.length
        ? item.data.exclude_urls.map((url) => {
            return { value: url };
          })
        : [],
      name: item.data.name || '',
      autoSync: trial ? !isTrialPlan : item.data.schedule ? item.data.schedule.enabled : true,
      recursive: item.data.recursive,
      interval: item.data.schedule
        ? isV1
          ? item.data.schedule.interval
          : WEBSITE_INTERVAL.WEEKLY
        : isV1
          ? WEBSITE_INTERVAL.MONTHLY
          : WEBSITE_INTERVAL.WEEKLY,
    });
  };

  useEffect(() => {
    if (isTrialPlan) {
      resetToInitial(true);
    }
  }, [isTrialPlan]);

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseCollector) {
      notification.success(`${name} Updated Successfully.`);
      closeSourceModal();
      close();
    }
  }, [isSuccessUpdateKnowledgeBaseCollector]);

  useEffect(() => {
    if (isLoadingUpdateKnowledgeBaseCollector) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateKnowledgeBaseCollector]);

  useEffect(() => {
    setSkipRefetchFiles(open);
  }, [open]);

  const submit = (data) => {
    let body;

    if (isV1) {
      body = {
        ...WEBSITE_INITIAL_REQUEST,
        ...item.data,
        name: data.name,
        urls: data?.urls?.length ? data.urls.map((url) => url.value.trim()) : [],
        exclude_urls: data?.exclude_urls?.length
          ? data.exclude_urls.map((url) => url.value.trim())
          : [],
        recursive: data.recursive,
        schedule: {
          interval: data.interval,
          enabled: data.autoSync,
          period: 'days',
        },
      };
    } else {
      body = {
        ...WEBSITE_INITIAL_REQUEST_V2,
        ...item.data,
        name: data.name,
        url: data.urls[0].value.trim(),
        exclude_urls: data?.exclude_urls?.length
          ? data.exclude_urls.map((url) => url.value.trim())
          : [],
        recursive: data.recursive,
        schedule: {
          interval: data.interval,
          enabled: data.autoSync,
          period: 'days',
        },
      };
    }

    delete body.run_immediately;

    updateKnowledgeBaseCollectorMutation({
      knowledgeBaseId: knowledgeBaseId || id,
      collectorId: item.data.id,
      body,
    });
  };

  const close = () => {
    resetToInitial();
    setOpen(false);
  };

  useEffect(() => {
    if (item && !open) {
      resetToInitial();
    }
  }, [item]);

  const onOpenChange = (isOpen) => {
    if (!isOpen) {
      resetToInitial();
    }

    setOpen(isOpen);
  };

  const isAutoSync = watch('autoSync');
  const name = watch('name');
  const recursiveWatch = watch('recursive');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger
          disabled={disabled}
          asChild
          className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
        >
          {trigger}
        </DialogTrigger>
        <DialogContent closeButtonHidden className="pb-0">
          <DialogHeader>
            <DialogTitle>Update website</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 pt-4">
            <div className="content">
              <form
                id="update-website-form"
                onSubmit={handleSubmit(submit)}
                className={'flex flex-col gap-5'}
              >
                <div className={'input-container'}>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        type={'text'}
                        label={'Name'}
                        placeholder={'CoSupport AI'}
                        invalidMessage={error?.message}
                        value={value}
                        isValid={!error?.message}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-3">
                  {fields.map((field, index) => (
                    <div key={field.id} className="relative">
                      <Controller
                        control={control}
                        name={`urls.${index}.value`}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            type="text"
                            label={'URL'}
                            placeholder="https://cosupport.ai"
                            invalidMessage={error?.message}
                            value={value}
                            isValid={!error?.message}
                            onChange={onChange}
                          />
                        )}
                      />
                      {fields.length > 1 && (
                        <button className="absolute top-1.5 right-0" onClick={() => remove(index)}>
                          <CloseIcon />
                        </button>
                      )}
                    </div>
                  ))}
                  {isV1 && (
                    <button
                      type="button"
                      onClick={() => append({ value: '' })}
                      className="font-medium text-xs text-[#7A6BE5] hover:underline self-start"
                    >
                      Add URL
                    </button>
                  )}
                </div>
                <div className={'flex flex-row gap-5'}>
                  <div className={'input-container'}>
                    <Controller
                      control={control}
                      name="recursive"
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <span className="flex flex-row items-center gap-4">
                          <span className="flex items-center gap-4">
                            <Switch type="black" checked={value} onCheckedChange={onChange} />
                          </span>
                          <span className={'flex flex-row items-center gap-2.5'}>
                            <span
                              className={'text-ebony_opacity_30 font-medium font-jamjuree text-sm'}
                            >
                              Sync Nested Pages
                            </span>
                            <InputInfoToolTip
                              contentClassName={'max-w-[300px]'}
                              infoToolTip={{
                                side: 'right',
                                description: (
                                  <>
                                    When enabled, the scraper will capture the main URL and all its
                                    subpages. If disabled, only the main URL will be synced.
                                  </>
                                ),
                              }}
                            />
                          </span>
                        </span>
                      )}
                    />
                  </div>
                </div>
                {recursiveWatch && (
                  <div className="flex flex-col gap-3">
                    {excludeFields.map((field, index) => (
                      <div key={field.id} className="relative">
                        <Controller
                          control={control}
                          name={`exclude_urls.${index}.value`}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                              type="text"
                              placeholder="https://www"
                              label={'Excluded URL'}
                              invalidMessage={error?.message}
                              value={value}
                              isValid={!error?.message}
                              onChange={onChange}
                            />
                          )}
                        />
                        <button
                          className="absolute top-1.5 right-0"
                          onClick={() => removeExclude(index)}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => appendExclude({ value: '' })}
                      className="font-medium text-xs text-[#7A6BE5] hover:underline self-start"
                    >
                      Add Exclude URL
                    </button>
                  </div>
                )}
                <div className={'flex flex-row gap-5'}>
                  <div className={'input-container'}>
                    <Controller
                      control={control}
                      name="autoSync"
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <span className="flex flex-row gap-4">
                          <span className="flex items-center gap-4">
                            <Switch
                              type="black"
                              checked={value}
                              onCheckedChange={onChange}
                              disabled={isTrialPlan}
                            />
                          </span>
                          <span
                            className={
                              'text-ebony_opacity_30 font-medium font-jamjuree text-sm flex flex-row items-center gap-2'
                            }
                          >
                            Auto-sync
                            {isTrialPlan && (
                              <InputInfoToolTip
                                infoToolTip={{
                                  side: 'right',
                                  description: <>Unavailable for trial plan</>,
                                }}
                              />
                            )}
                          </span>
                        </span>
                      )}
                    />
                  </div>
                  {isAutoSync && (
                    <div className={'input-container'}>
                      <Controller
                        control={control}
                        name="interval"
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <RadioGroup
                            onChange={onChange}
                            onValueChange={onChange}
                            defaultValue={value as any}
                            disabled={isTrialPlan || !isV1}
                            className="flex flex-row gap-5 items-center"
                          >
                            <div className="flex items-center space-x-[7px] space-y-0">
                              <RadioGroupItem
                                value={WEBSITE_INTERVAL.WEEKLY as any}
                                id={'interval-weekly'}
                              />
                              <label
                                htmlFor={'interval-weekly'}
                                className={clsx(!(isTrialPlan || !isV1) && 'cursor-pointer')}
                              >
                                <p
                                  className={clsx(
                                    'font-medium text-sm text-ebony opacity-50 transition',
                                    !(isTrialPlan || !isV1) && 'hover:opacity-100',
                                  )}
                                >
                                  Weekly
                                </p>
                              </label>
                            </div>
                            <div className="flex items-center space-x-[7px] space-y-0">
                              <RadioGroupItem
                                value={WEBSITE_INTERVAL.MONTHLY as any}
                                id={'interval-monthly'}
                              />
                              <label
                                htmlFor={'interval-monthly'}
                                className={clsx(!(isTrialPlan || !isV1) && 'cursor-pointer')}
                              >
                                <p
                                  className={clsx(
                                    'font-medium text-sm text-ebony opacity-50 transition',
                                    !(isTrialPlan || !isV1) && 'hover:opacity-100',
                                  )}
                                >
                                  Monthly
                                </p>
                              </label>
                            </div>
                          </RadioGroup>
                        )}
                      />
                    </div>
                  )}
                </div>
                <div className="sticky bottom-0 w-full bg-white">
                  <div className="flex justify-center my-5">
                    <div className="flex gap-2.5">
                      <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                        Cancel
                      </Button>
                      <Button disabled={!isDirty} type="submit" className="min-w-24">
                        Update
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
